/**
 * The customer signature (piece 27, Customer signature; decisions 0009, 0519).
 *
 * 🔨 The document records that it was signed, never how it was routed. The
 * manual and the electronic path write one identical `DocumentSignature` row
 * and both freeze the document revision, and nothing downstream branches on
 * `method`. 🔨 `method` is shown wherever a signature is shown and never
 * collapsed into a generic Signed badge: an electronic signature carries an
 * identity trail and a tamper-evident certificate, and a manual one carries a
 * colleague's word.
 */
import { isEmailAddress } from '../validators';

/**
 * The five documents a signature can be recorded against, the register's own
 * targets. 🔒 The Work Order joined as the fifth (decision 2060): its visit's
 * service report carries the customer's and the lead worker's rows.
 */
export const SIGNABLE_DOCUMENT_TYPES = ['Quote', 'SalesOrder', 'Invoice', 'ChangeOrder', 'WorkOrder'] as const;
export type SignableDocumentType = (typeof SIGNABLE_DOCUMENT_TYPES)[number];

/** 🔒 InPerson is drawn on the screen the signer is shown, and its email is optional (decision 2060). */
export const SIGNATURE_METHODS = ['Electronic', 'Manual', 'InPerson'] as const;
export type SignatureMethodValue = (typeof SIGNATURE_METHODS)[number];

export function isSignableDocumentType(value: string): value is SignableDocumentType {
  return (SIGNABLE_DOCUMENT_TYPES as readonly string[]).includes(value);
}

/** What a signature reads as on a screen; 🔨 never the word Signed alone. */
export function signatureMethodLabel(method: SignatureMethodValue): string {
  if (method === 'InPerson') return 'Signed in person';
  return method === 'Electronic' ? 'Signed electronically' : 'Signed on paper';
}

/** The one line beneath it saying what the evidence is, so the two are not presented as equal. */
export function signatureEvidenceLine(method: SignatureMethodValue): string {
  if (method === 'InPerson') return 'Drawn on the screen in front of a colleague; the evidence is the sheet frozen with the drawn signature.';
  return method === 'Electronic'
    ? 'The vendor’s identity trail and tamper-evident certificate stand behind this.'
    : 'A colleague attested the returned copy; the evidence is the file and their word.';
}

/** The row both paths write, which is what a test compares across them. */
export interface SignatureRow {
  documentType: SignableDocumentType;
  documentId: string;
  documentRevision: number;
  method: SignatureMethodValue;
  signedAt: Date;
  signerName: string;
  /** Null on an InPerson row whose signer gave none (decision 2060). */
  signerEmail: string | null;
  evidenceFileId: string;
  recordedByPersonId: string | null;
  vendorEnvelopeId: string | null;
}

export interface SignatureInput {
  documentType: string;
  documentId: string;
  documentRevision: number;
  method: SignatureMethodValue;
  signedAt?: Date | null;
  signerName?: string | null;
  signerEmail?: string | null;
  evidenceFileId?: string | null;
  recordedByPersonId?: string | null;
  vendorEnvelopeId?: string | null;
}

/**
 * Everything both paths must carry, and the one field each path carries alone.
 * 🔨 A Manual row names its attesting colleague and no envelope; an Electronic
 * row names its envelope and no attester. The database carries the same check.
 */
export function signatureProblem(input: SignatureInput): string | null {
  if (!isSignableDocumentType(input.documentType)) return `${input.documentType} carries no signature; a Quote, a Sales Order, an Invoice, a Change Order or a Work Order does`;
  if (!input.documentId) return 'a signature names the document it was given on';
  if (!Number.isInteger(input.documentRevision) || input.documentRevision < 0) return 'a signature freezes the revision it was given on';
  if (!(SIGNATURE_METHODS as readonly string[]).includes(input.method)) return 'a signature is Electronic, Manual or In person';
  if (!input.signerName || !input.signerName.trim()) return 'a signature names who signed';
  // 🔒 Decision 2060: the in-person signer may give no address; one given is still an address.
  const email = input.signerEmail?.trim() ?? '';
  if (input.method === 'InPerson' ? email !== '' && !isEmailAddress(email) : !isEmailAddress(email)) return 'a signature carries the signer’s email address';
  if (!input.evidenceFileId) return 'a signature names the file that is the evidence for it';
  if (!input.signedAt || Number.isNaN(input.signedAt.getTime())) return 'a signature carries the moment it was signed';
  if (input.method === 'Manual') {
    if (!input.recordedByPersonId) return 'a signature recorded by hand names the colleague who attested it';
    if (input.vendorEnvelopeId) return 'a signature recorded by hand carries no vendor envelope';
  } else if (input.method === 'InPerson') {
    if (!input.recordedByPersonId) return 'a signature drawn in person names the colleague holding the device';
    if (input.vendorEnvelopeId) return 'a signature drawn in person carries no vendor envelope';
  } else {
    if (!input.vendorEnvelopeId) return 'an electronic signature names the vendor envelope it came from';
    if (input.recordedByPersonId) return 'an electronic signature carries no attesting colleague';
  }
  return null;
}

/**
 * 🔒 The row itself, identical either way but for the one path-specific field.
 * Downstream behaviour — the Accepted status, the Sales Order conversion, the
 * audit trail — reads this and never branches on `method`.
 */
export function signatureRowOf(input: SignatureInput): SignatureRow {
  const problem = signatureProblem(input);
  if (problem) throw new SignatureError(problem);
  return {
    documentType: input.documentType as SignableDocumentType,
    documentId: input.documentId,
    documentRevision: input.documentRevision,
    method: input.method,
    signedAt: input.signedAt!,
    signerName: input.signerName!.trim(),
    signerEmail: input.signerEmail?.trim() || null,
    evidenceFileId: input.evidenceFileId!,
    recordedByPersonId: input.method === 'Electronic' ? null : input.recordedByPersonId!,
    vendorEnvelopeId: input.method === 'Electronic' ? input.vendorEnvelopeId! : null,
  };
}

export class SignatureError extends Error {}

/**
 * The fields the two paths must agree on, which is what makes the rule
 * checkable rather than a claim: everything but `method`, the attester and the
 * envelope.
 */
export const PATH_INDEPENDENT_SIGNATURE_FIELDS = ['documentType', 'documentId', 'documentRevision', 'signedAt', 'signerName', 'signerEmail', 'evidenceFileId'] as const;

/** 🔨 Two rows written by the two paths for the same signing are identical on every field that is not the path. */
export function signaturesAgreeApartFromPath(a: SignatureRow, b: SignatureRow): boolean {
  return PATH_INDEPENDENT_SIGNATURE_FIELDS.every((field) => {
    const left = a[field];
    const right = b[field];
    return left instanceof Date && right instanceof Date ? left.getTime() === right.getTime() : left === right;
  });
}

// ---- The electronic path's own vocabulary ----

/** What a vendor callback can say happened; a completion is the only one that signs. */
export const ENVELOPE_OUTCOMES = ['Completed', 'Declined', 'Expired', 'Voided'] as const;
export type EnvelopeOutcome = (typeof ENVELOPE_OUTCOMES)[number];

export function isEnvelopeOutcome(value: string): value is EnvelopeOutcome {
  return (ENVELOPE_OUTCOMES as readonly string[]).includes(value);
}

/** 🔨 Only a completion advances the document; the other three close the envelope and move nothing. */
export function outcomeSigns(outcome: EnvelopeOutcome): boolean {
  return outcome === 'Completed';
}

/** The sentence the document's own timeline carries for each outcome, so a decline is visible rather than silent. */
export function envelopeOutcomeLine(outcome: EnvelopeOutcome, signerName: string): string {
  if (outcome === 'Completed') return `${signerName} signed electronically.`;
  if (outcome === 'Declined') return `${signerName} declined to sign. The document is unchanged and can be sent again.`;
  if (outcome === 'Expired') return `The signing envelope expired before ${signerName} signed. Send it again to reopen one.`;
  return `The signing envelope was withdrawn before ${signerName} signed.`;
}

/** The header the callback's digest rides in (decision 0519). */
export const E_SIGNATURE_SIGNATURE_HEADER = 'x-bms2-signature';
/** The environment variable the secret is read from; a missing one refuses every callback. */
export const E_SIGNATURE_SECRET_ENV = 'E_SIGNATURE_WEBHOOK_SECRET';

/**
 * 🔒 Why a callback was refused, as a sentence, or nothing where it passes. The
 * digest itself is compared by the caller, which holds the secret; this names
 * the two refusals that come before the comparison.
 */
export function callbackRefusal(facts: { secretConfigured: boolean; headerPresent: boolean; digestMatches: boolean }): string | null {
  if (!facts.secretConfigured) return 'no signing secret is configured, so no callback can be trusted; set the e-signature webhook secret';
  if (!facts.headerPresent) return 'the callback carries no signature header and is refused without being read';
  if (!facts.digestMatches) return 'the callback’s signature does not match its body and is refused';
  return null;
}
